import { useCallback } from 'react'
import type { RefObject } from 'react'
import { setClockOverride } from '../../../lib/clock'
import { useClock } from '../../../hooks/useClock'
import {
  DEFAULT_GRID_GEOMETRY,
  festivalMinutesToDate,
  festivalMinutesNow,
  festivalMinutesToTop,
  minutesToClockLabel,
} from './timeUtils'
import type { GridGeometry } from './timeUtils'
import { useDayCutoff } from '../../../contexts/DayCutoffContext'
import { useTimeFormat } from '../../../hooks/useTimeFormat'
import { useLineDrag } from './useLineDrag'

// Props ready to spread onto <NowIndicator>.
export interface NowIndicatorProps {
  top: number
  draggable: boolean
  dragging: boolean
  timeLabel?: string
  onHandlePointerDown: (e: React.PointerEvent) => void
}

// Encapsulates the "drag the now-line to retime the clock override" gesture.
// Only active while a clock override is in effect; otherwise the indicator is
// returned as a plain, non-interactive line. The pointer→time math and the
// in-flight preview live in useLineDrag; this adds the commit to the clock.
//
// The commit goes through the clock lib (not the context setter) so the override
// updates in place — the caller's reactive `now` re-renders the line at the
// dropped position without remounting the grid (preserves scroll + selected day).
export function useNowLineDrag({
  now,
  nowLineMin,
  gridMin,
  gridMax,
  day,
  timezone,
  gridRef,
  geometry = DEFAULT_GRID_GEOMETRY,
}: {
  /** Reactive effective "now" (override-aware), used for the resting label. */
  now: Date
  /** Grid-minute of the live now-line, or null when "now" is off this grid. */
  nowLineMin: number | null
  gridMin: number
  gridMax: number
  day: string
  timezone?: string
  /** The scrolling grid element, owned by DayGrid, used for pointer→time math. */
  gridRef: RefObject<HTMLDivElement | null>
  /** Rendered row-track sizes, so px math matches the laid-out gridlines. */
  geometry?: GridGeometry
}): {
  indicator: NowIndicatorProps | null
} {
  const cutoff = useDayCutoff()
  const { override } = useClock()
  const { hourCycle, locale } = useTimeFormat()
  const commit = useCallback(
    (min: number) => setClockOverride(festivalMinutesToDate(min, day, timezone)),
    [day, timezone],
  )
  const { dragMin, startDrag: startLineDrag } = useLineDrag({ gridRef, gridMin, gridMax, geometry, onCommit: commit })
  const startDrag = useCallback(
    (e: React.PointerEvent) => {
      if (override) startLineDrag(e)
    },
    [override, startLineDrag],
  )

  const min = dragMin ?? nowLineMin
  if (min === null) return { indicator: null }
  const top = festivalMinutesToTop(min, gridMin, geometry.slotPx)

  return {
    indicator: {
      top,
      draggable: !!override,
      dragging: dragMin !== null,
      timeLabel: override
        ? minutesToClockLabel(dragMin ?? festivalMinutesNow(now, cutoff, timezone), hourCycle, locale)
        : undefined,
      onHandlePointerDown: startDrag,
    },
  }
}
